import React, { useState } from 'react';
import { X, Mail, AlertCircle, Send, CheckCircle } from 'lucide-react';

function ContactModal({ isOpen, onClose }) {
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    
    const form = e.target;
    const formData = new FormData(form);
    
    try {
      const response = await fetch('https://formspree.io/f/xeenbyko', {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (response.ok) {
        setSubmitted(true);
        form.reset();
        setTimeout(() => {
          setSubmitted(false);
          onClose();
        }, 2000);
      } else {
        const data = await response.json();
        if (data.errors) {
          setError(data.errors.map(error => error.message).join(', '));
        } else {
          setError('Erreur lors de l\'envoi. Veuillez réessayer.');
        }
      }
    } catch (err) {
      setError('Erreur de connexion. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  if (submitted) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-dark-bg/90 backdrop-blur-md">
        <div className="bg-dark-surface border border-white/10 rounded-2xl max-w-lg w-full p-12 text-center">
          <div className="w-20 h-20 bg-mint/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle size={40} className="text-mint" />
          </div>
          <h3 className="text-2xl font-mono text-white mb-2">Message envoyé !</h3>
          <p className="text-white/40 text-sm">Nous traiterons votre demande rapidement.</p>
          <button onClick={onClose} className="mt-6 bg-mint text-dark-bg px-6 py-2 rounded-lg font-mono text-sm">
            Fermer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-dark-bg/90 backdrop-blur-md">
      <div className="bg-dark-surface border border-white/10 rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        
        <div className="relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-mint/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-pink/10 rounded-full blur-3xl"></div>
          <div className="flex justify-between items-center p-6 border-b border-white/10 relative z-10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-mint to-pink flex items-center justify-center">
                <Mail size={18} className="text-dark-bg" />
              </div>
              <div>
                <h2 className="text-xl font-serif text-white">Signaler un problème</h2>
                <p className="text-white/40 text-sm mt-0.5">Nous vous répondrons dans les plus brefs délais</p>
              </div>
            </div>
            <button 
              onClick={onClose} 
              className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 transition-colors flex items-center justify-center text-white/40 hover:text-white"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <input type="hidden" name="_subject" value="ISIMM Archive - Signalement utilisateur" />
          <input type="hidden" name="_captcha" value="false" />
          
          <div>
            <label className="block text-white/70 text-sm font-mono mb-2">Votre nom *</label>
            <input
              type="text"
              name="name"
              required
              className="w-full px-4 py-3 bg-dark-card border border-white/10 rounded-xl text-white placeholder:text-white/20 focus:outline-none focus:border-mint focus:ring-1 focus:ring-mint transition-all"
              placeholder="Jean Dupont"
            />
          </div>

          <div>
            <label className="block text-white/70 text-sm font-mono mb-2">Votre email *</label>
            <input
              type="email"
              name="email"
              required
              className="w-full px-4 py-3 bg-dark-card border border-white/10 rounded-xl text-white placeholder:text-white/20 focus:outline-none focus:border-mint focus:ring-1 focus:ring-mint transition-all"
              placeholder="jean.dupont@example.com"
            />
          </div>

          <div>
            <label className="block text-white/70 text-sm font-mono mb-2">Sujet *</label>
            <select
              name="subject"
              required
              className="w-full px-4 py-3 bg-dark-card border border-white/10 rounded-xl text-white focus:outline-none focus:border-mint focus:ring-1 focus:ring-mint transition-all"
            >
              <option value="">Sélectionner un sujet</option>
              <option value="Problème d'affichage">Problème d'affichage</option>
              <option value="Document manquant">Document manquant</option>
              <option value="Erreur dans un document">Erreur dans un document</option>
              <option value="Proposition de contenu">Proposition de contenu</option>
              <option value="Autre">Autre</option>
            </select>
          </div>

          <div>
            <label className="block text-white/70 text-sm font-mono mb-2">Message *</label>
            <textarea
              name="message"
              required
              rows={5}
              className="w-full px-4 py-3 bg-dark-card border border-white/10 rounded-xl text-white placeholder:text-white/20 focus:outline-none focus:border-mint focus:ring-1 focus:ring-mint transition-all resize-none"
              placeholder="Décrivez votre problème..."
            />
          </div>

          {error && (
            <div className="bg-pink/10 border border-pink/30 rounded-xl p-3 flex items-center gap-2">
              <AlertCircle size={16} className="text-pink" />
              <p className="text-pink text-xs">{error}</p>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 border border-white/20 rounded-xl text-white/60 hover:border-white/40 hover:text-white transition-all font-mono text-sm"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 bg-gradient-to-r from-mint to-pink text-dark-bg px-4 py-3 rounded-xl font-mono text-sm font-semibold hover:shadow-lg hover:shadow-mint/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-dark-bg border-t-transparent rounded-full animate-spin" />
                  Envoi...
                </>
              ) : (
                <>
                  <Send size={14} />
                  Envoyer
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ContactModal;